import { Box, ButtonBase } from "@mui/material";
import { brainTokens } from "../../../design/brainTheme";
import type { ProjectJiraIssueRow } from "../../../lib/brain-api";

const c = brainTokens.color;

function segmentColor(status: string): string {
  const s = status.trim().toLowerCase();
  if (s === "complete" || s === "done" || s === "closed") return c.successDot;
  if (s === "in progress" || s === "in review" || s === "moving…") return c.warnDot;
  if (s === "planning") return c.teal;
  if (s === "to do" || s === "todo" || s === "open") return c.tealInk2;
  if (s === "backlog") return c.placeholder;
  return c.faint2;
}

export type StatusSegment = {
  label: string;
  count: number;
  color: string;
};

export function buildStatusSpread(issues: ProjectJiraIssueRow[]): StatusSegment[] {
  const byStatus = new Map<string, number>();
  for (const i of issues) {
    const label = (i.status || "Unknown").trim() || "Unknown";
    byStatus.set(label, (byStatus.get(label) ?? 0) + 1);
  }
  return [...byStatus.entries()]
    .map(([label, count]) => ({ label, count, color: segmentColor(label) }))
    .sort((a, b) => b.count - a.count);
}

export function StatusSpread({
  issues,
  boardCount,
  onOpenBoard,
}: {
  issues: ProjectJiraIssueRow[];
  boardCount: number;
  onOpenBoard: () => void;
}) {
  const segments = buildStatusSpread(issues);
  const total = issues.length;
  const boardLabel =
    boardCount <= 0
      ? total > 0
        ? `across ${total} issues`
        : "no issues in scope"
      : `across ${total} issues on ${boardCount} Jira board${boardCount === 1 ? "" : "s"}`;

  return (
    <Box
      sx={{
        background: c.white,
        border: `1px solid ${c.border}`,
        borderRadius: `${brainTokens.radius.card}px`,
        padding: "20px 22px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "12px" }}>
        <Box
          component="span"
          sx={{
            fontFamily: brainTokens.font.mono,
            fontSize: 10,
            letterSpacing: "0.12em",
            color: c.faint,
            whiteSpace: "nowrap",
          }}
        >
          STATUS SPREAD
        </Box>
        <Box component="span" sx={{ fontSize: 12, color: c.muted }}>
          {boardLabel}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box
          component={ButtonBase}
          onClick={onOpenBoard}
          sx={{
            fontSize: 13,
            color: c.tealInk,
            fontWeight: 500,
            whiteSpace: "nowrap",
            fontFamily: "inherit",
            lineHeight: 1.2,
          }}
        >
          Open board →
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          height: 8,
          borderRadius: `${brainTokens.radius.pill}px`,
          overflow: "hidden",
          mt: "9px",
          background: c.borderLight,
        }}
      >
        {segments.map((s) => (
          <Box
            key={s.label}
            title={`${s.label} · ${s.count}`}
            sx={{
              height: "100%",
              width: `${total > 0 ? (s.count / total) * 100 : 0}%`,
              background: s.color,
              flex: "none",
            }}
          />
        ))}
      </Box>

      {segments.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px 18px", mt: "10px" }}>
          {segments.map((s) => (
            <Box
              key={s.label}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "7px",
                fontSize: 12,
                color: c.muted,
                whiteSpace: "nowrap",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: `${brainTokens.radius.pill}px`,
                  background: s.color,
                  flex: "none",
                }}
              />
              <Box component="span">{s.label}</Box>
              <Box
                component="span"
                sx={{ fontFamily: brainTokens.font.mono, fontSize: 11.5, color: c.ink3 }}
              >
                {s.count}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
